import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  Login as LoginIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as ActiveIcon,
  WarningAmber as StaleIcon,
  NoAccounts as NeverLoggedIcon,
  Assignment as RolesIcon
} from '@mui/icons-material';
import { api } from '../services/firebaseFunctions';
import PageHeader from '../interaction/PageHeader';

function Dashboard({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState({
    users: [],
    companies: [],
    roles: []
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setError(null);

      const [companiesResult, rolesResult, usersResult] = await Promise.allSettled([
        api.listCompanies(),
        api.listRoles(),
        api.listUsers()
      ]);

      setData({
        users: usersResult.status === 'fulfilled' ? usersResult.value : [],
        companies: companiesResult.status === 'fulfilled' ? companiesResult.value : [],
        roles: rolesResult.status === 'fulfilled' ? rolesResult.value : []
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(`Failed to load dashboard data: ${err.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  // Derived stats
  const activeUsers = data.users.filter(u => u.status === 'active');
  const pendingUsers = data.users.filter(u => u.status === 'pending');
  const inactiveUsers = data.users.filter(u => u.status === 'inactive');

  // Users by company
  const usersByCompany = {};
  data.users.forEach(u => {
    const key = u.company_name || u.company || 'Unassigned';
    usersByCompany[key] = (usersByCompany[key] || 0) + 1;
  });
  const companyBreakdown = Object.entries(usersByCompany)
    .sort((a, b) => b[1] - a[1]);

  // Logins in the last 7 days
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const recentLogins = data.users
    .filter(u => u.lastSignIn && new Date(u.lastSignIn) > sevenDaysAgo)
    .sort((a, b) => new Date(b.lastSignIn) - new Date(a.lastSignIn));

  // Never logged in (active users who haven't signed in yet)
  const neverLoggedIn = data.users
    .filter(u => !u.lastSignIn && u.status === 'active');

  // Stale users (haven't logged in for 30+ days)
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const staleUsers = data.users
    .filter(u => u.lastSignIn && new Date(u.lastSignIn) < thirtyDaysAgo && u.status === 'active')
    .sort((a, b) => new Date(a.lastSignIn) - new Date(b.lastSignIn));

  // Roles distribution
  const rolesDistribution = data.roles
    .map(role => ({
      ...role,
      userCount: data.users.filter(u => (u.roles || []).includes(role.id)).length
    }))
    .sort((a, b) => b.userCount - a.userCount);

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Dashboard</Typography>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" height={40} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle="Where access stands right now across users, companies and pending requests."
        className="ph--page"
        slot={
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            Refresh
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            title="Total Users"
            value={data.users.length}
            icon={<PeopleIcon />}
            color="primary"
            onClick={() => navigate('/users')}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            title="Active"
            value={activeUsers.length}
            icon={<ActiveIcon />}
            color="success"
            onClick={() => navigate('/users')}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            title="Pending"
            value={pendingUsers.length}
            icon={<PendingIcon />}
            color="warning"
            onClick={() => navigate('/users')}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            title="Companies"
            value={data.companies.length}
            icon={<BusinessIcon />}
            color="info"
            onClick={() => navigate('/companies')}
          />
        </Grid>
      </Grid>

      {/* Pending Users Alert */}
      {pendingUsers.length > 0 && (
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'warning.light' }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <PersonAddIcon color="warning" sx={{ mr: 1 }} />
              <Typography variant="h6">
                Pending Users ({pendingUsers.length})
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These users have been created but haven't signed in yet. Consider sending them a welcome email.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Roles</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingUsers.map(u => (
                    <TableRow key={u.uid}>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.company_name || u.company || '—'}</TableCell>
                      <TableCell>
                        {(u.roles || []).map(r => (
                          <Chip key={r} label={r} size="small" sx={{ mr: 0.5 }} />
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Two-column layout: Recent Logins + Users by Company */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Recent Logins */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <LoginIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="h6">Logins (Last 7 Days)</Typography>
              </Box>
              {recentLogins.length > 0 ? (
                <List disablePadding sx={{ maxHeight: 360, overflow: 'auto' }}>
                  {recentLogins.map(u => (
                    <ListItem key={u.uid} disableGutters divider sx={{ py: 1 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: 'primary.main' }}>
                          {(u.email || '?')[0].toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={u.displayName || u.email}
                        secondary={new Date(u.lastSignIn).toLocaleString()}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <Chip
                        label={u.company_name || u.company || '—'}
                        size="small"
                        variant="outlined"
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" textAlign="center" py={4}>
                  No login data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Users by Company */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="h6">Users by Company</Typography>
              </Box>
              {companyBreakdown.length > 0 ? (
                <List disablePadding>
                  {companyBreakdown.map(([company, count]) => (
                    <ListItem key={company} disableGutters divider sx={{ py: 1 }}>
                      <ListItemText
                        primary={company}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <Chip
                        label={count}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" textAlign="center" py={4}>
                  No data
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bottom row: Stale Users + Never Logged In + Roles Distribution */}
      <Grid container spacing={3}>
        {/* Stale Users */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <StaleIcon sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">Inactive 30+ Days</Typography>
              </Box>
              {staleUsers.length > 0 ? (
                <List disablePadding sx={{ maxHeight: 280, overflow: 'auto' }}>
                  {staleUsers.map(u => (
                    <ListItem key={u.uid} disableGutters divider sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={u.displayName || u.email}
                        secondary={`Last login: ${new Date(u.lastSignIn).toLocaleDateString()}`}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" variant="body2" textAlign="center" py={3}>
                  All users active recently
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Never Logged In */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <NeverLoggedIcon sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6">Never Logged In</Typography>
              </Box>
              {neverLoggedIn.length > 0 ? (
                <List disablePadding sx={{ maxHeight: 280, overflow: 'auto' }}>
                  {neverLoggedIn.map(u => (
                    <ListItem key={u.uid} disableGutters divider sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={u.email}
                        secondary={u.company_name || u.company || '—'}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" variant="body2" textAlign="center" py={3}>
                  All active users have logged in
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Roles Distribution */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <RolesIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="h6">Roles Distribution</Typography>
              </Box>
              {rolesDistribution.length > 0 ? (
                <List disablePadding sx={{ maxHeight: 280, overflow: 'auto' }}>
                  {rolesDistribution.map(role => (
                    <ListItem key={role.id} disableGutters divider sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={role.name || role.id}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <Chip
                        label={role.userCount}
                        size="small"
                        color={role.userCount > 0 ? 'primary' : 'default'}
                        variant={role.userCount > 0 ? 'filled' : 'outlined'}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" variant="body2" textAlign="center" py={3}>
                  No roles defined
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Box>
  );
}

function StatCard({ title, value, icon, color, onClick }) {
  return (
    <Card
      sx={{
        height: '100%',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="text.secondary" variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {value}
            </Typography>
          </Box>
          <Avatar
            sx={{
              bgcolor: `${color}.main`,
              width: 48,
              height: 48
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
}

export default Dashboard;
